import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  campanhaEditavel, cancelarCampanha, codigoDaCampanha, verCampanha,
  type CampanhaCompleta, type RelatorioCompleto,
} from '../../api/cptu';
import { useCaminho } from '../../Caminho';
import { data, plural } from '../../formato';
import { useSessao } from '../../sessao';
import { degrauDe, SITUACAO_DA_CAMPANHA_CPTU } from '../../situacoes';
import {
  AcaoBloqueada, Area, Aviso, Carregando, Dialogo, ErroDaApi, SeloDaEscada, useEstreito,
} from '../../ui';
import { MenuDaOs } from '../OsMoldura';
import { numeroBr } from '../../cptu/visual';
import { type VistaDaAnalise } from './analise';
import { CampanhaAnalise } from './CampanhaAnalise';
import { CampanhaDissipacoes } from './CampanhaDissipacoes';
import { CampanhaHistorico } from './CampanhaHistorico';
import { CampanhaPremissas } from './CampanhaPremissas';
import { CampanhaRelatorio } from './CampanhaRelatorio';
import { CampanhaVerticais } from './CampanhaVerticais';
import { DissipacaoInterpretada } from './DissipacaoInterpretada';
import { revisao, revisaoAberta } from './FluxoDoRelatorio';
import { FormularioDaJanela } from './FormularioDaJanela';
import { excecoesDaVertical, type VistaDaVertical } from './interpretacao';
import { VerticalInterpretada } from './VerticalInterpretada';

/**
 * A casca da campanha CPTu — onda 312, Tarefa 312.5 (spec 07 §4; RN-253).
 *
 * O molde é o da OS (onda 256, P-UI-33): o cabeçalho com o código em fonte
 * de código, o selo da situação e UMA ação primária («Importar arquivos»
 * enquanto a campanha muda); a régua das etapas; as abas como links, cada
 * uma com endereço próprio (F5 e «voltar» mantêm a aba):
 *
 *   /cptu/:id              Verticais — a aba padrão
 *   /cptu/:id/dissipacoes  Dissipações (e /:did, a dissipação aberta — 313)
 *   /cptu/:id/premissas    Premissas (313)
 *   /cptu/:id/relatorio    Relatório (315)
 *   /cptu/:id/historico    Histórico — a trilha da campanha
 *
 * R11: a casca nasce com Verticais · Dissipações · Histórico; Premissas,
 * Análise integrada e Relatório entram com as ondas delas (313, 316, 315).
 * A régua nasce inteira (importada → processada → verificação → aprovação
 * do RT → emitido → no Portal), com «importada» feita quando há vertical
 * ativa e o resto pendente.
 *
 * Onda 313 (Tarefa 313.4, R3): a vertical aberta é sub-rota da aba
 * Verticais — `/cptu/:id/verticais/:vid` (Perfis) e `…/tabela` —, e a casca
 * a desenha no lugar da tabela, com o caminho «Campanha › Vertical CPTu01».
 * A régua (R16): «Processada» passa a «em andamento» quando há
 * interpretação gravada PELA TELA — premissas da campanha, exceção numa
 * vertical (fora o NA e a cota), edição, camada vigente ou uma escolha de
 * dissipação gravada depois da importação; «feito» só no envio para a
 * verificação (onda 315).
 *
 * Tarefa 313.6 (R3): as vistas Edição e Camadas da vertical — `…/edicao` e `…/camadas` —, também sub-rotas da
 * aba Verticais; com o formulário delas, «Importar arquivos» passa a secundário (P-UI-10).
 *
 * Tarefa 313.5 (R3): a aba Premissas (`/cptu/:id/premissas`) entre Dissipações
 * e Histórico, e a dissipação aberta como sub-rota da aba Dissipações —
 * `/cptu/:id/dissipacoes/:did`, com o caminho «Campanha › Dissipação CPTu01 ·
 * 6,50 m». Com a dissipação aberta, ou na aba Premissas, o primário da tela é o
 * «Gravar»/«Aplicar e recalcular» dela: «Importar arquivos» passa a secundário
 * (P-UI-10).
 *
 * Onda 315 (Tarefa 315.5): a aba Relatório (`/cptu/:id/relatorio`) depois de
 * Premissas e antes de Histórico (a Análise integrada da 316 entra antes
 * dela).
 *
 * Onda 316 (Tarefa 316.2, R2): a aba Análise integrada (`/cptu/:id/analise`, com
 * as vistas `…/analise/perfis` e, na 316.3, `…/analise/3d`) entre Premissas e
 * Relatório — a ordem da prancheta e da 315. Nela o primário é «Gravar a linha
 * A–B»: «Importar arquivos» passa a secundário (P-UI-10). Na aba, o primário da tela é o do fluxo do relatório (enviar,
 * verificar, aprovar e emitir, disponibilizar): «Importar arquivos» passa a
 * secundário (P-UI-10). A régua lê o estado do relatório que a aba carregou
 * (o `GET …/relatorio` monta o documento — não se pede em toda aba): a
 * «Processada» fica «feito» desde o envio; Verificação, Aprovação do RT,
 * Emitido («✓ <OS>-CPTU-01 R00 · dd/mm/aaaa») e No Portal com quem e quando.
 * Sem o relatório carregado, a régua diz o que a situação da campanha sabe.
 *
 * As classes são as da OS (`cabeca-os`, `regua-os`, `abas-os`): a mesma
 * moldura de registro, desenhada uma vez só na folha.
 */

export type AbaDaCampanha = 'verticais' | 'dissipacoes' | 'premissas' | 'analise' | 'relatorio' | 'historico';

export function rotaDaAbaDaCampanha(id: string, aba: AbaDaCampanha): string {
  return aba === 'verticais' ? `/cptu/${id}` : `/cptu/${id}/${aba}`;
}

const NOME_DA_ABA: { [aba in AbaDaCampanha]: string } = {
  verticais: 'Verticais', dissipacoes: 'Dissipações', premissas: 'Premissas', analise: 'Análise integrada', relatorio: 'Relatório',
  historico: 'Histórico',
};

/** O selo da situação da campanha: símbolo, palavra e tom. */
export function SeloDaCampanha({ situacao }: { situacao: string }) {
  return <SeloDaEscada degrau={degrauDe(SITUACAO_DA_CAMPANHA_CPTU, situacao)} />;
}

/** A rota da casca: carrega a campanha e desenha a aba pedida. */
export function CampanhaTela({ aba, vista, vistaDaAnalise }: { aba: AbaDaCampanha; vista?: VistaDaVertical; vistaDaAnalise?: VistaDaAnalise }) {
  const { id = '', vid, did } = useParams();
  return (
    <Casca key={id} id={id} aba={aba} vid={aba === 'verticais' ? vid ?? null : null}
      did={aba === 'dissipacoes' ? did ?? null : null} vista={vista ?? 'perfis'} vistaDaAnalise={vistaDaAnalise ?? 'mapa'} />
  );
}

function Casca({ id, aba, vid, did, vista, vistaDaAnalise }: {
  id: string; aba: AbaDaCampanha; vid: string | null; did: string | null; vista: VistaDaVertical; vistaDaAnalise: VistaDaAnalise;
}) {
  const [c, setC] = useState<CampanhaCompleta | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const local = useLocation();
  const navegar = useNavigate();
  /*
   * O «Gravado.» da importação vem no estado da navegação, uma vez só: ele
   * fica aqui, e o estado sai do histórico — o F5 e o «voltar» não o repetem.
   */
  const [gravado, setGravado] = useState<string | null>(
    () => (local.state as { gravado?: string } | null)?.gravado ?? null);
  useEffect(() => {
    if ((local.state as { gravado?: string } | null)?.gravado) {
      navegar(`${local.pathname}${local.search}`, { replace: true, state: null });
    }
  }, [local.state, local.pathname, local.search, navegar]);
  /* Trocar de aba encerra o recado: voltar a Verticais não o repete. */
  useEffect(() => { if (aba !== 'verticais') setGravado(null); }, [aba]);
  /* Com o «Simular» aberto na vertical — ou nas vistas Edição e Camadas (313.6), com o «Gravar» delas —, o primário da
     tela é o «Gravar»: o «Importar» passa a secundário (P-UI-10). */
  const [simulando, setSimulando] = useState(false);
  /* O relatório que a aba Relatório carregou (onda 315): a régua o lê, também nas outras abas. */
  const [relatorio, setRelatorio] = useState<RelatorioCompleto | null>(null);

  const carregar = useCallback(async () => {
    try { setC(await verCampanha(id)); setErro(null); } catch (e) { setErro(e); }
  }, [id]);
  useEffect(() => { void carregar(); }, [carregar]);

  const aberta = vid && c ? c.verticais.find((v) => v.id === vid) ?? null : null;
  const dissipacao = did && c ? c.dissipacoes.find((d) => d.id === did) ?? null : null;
  useCaminho(c ? [
    { rotulo: 'Campanha', codigo: `${codigoDaCampanha(c.campanha.sequencia)} · OS ${c.os.numero}`,
      para: aba === 'verticais' && !vid ? undefined : `/cptu/${id}` },
    ...(vid ? [{ rotulo: 'Vertical', codigo: aberta?.codigo }]
      : did ? [{
        rotulo: 'Dissipação',
        codigo: dissipacao ? `${dissipacao.vertical} · ${numeroBr(dissipacao.profundidade, 2)} m` : undefined,
      }]
        : aba === 'verticais' ? [] : [{ rotulo: NOME_DA_ABA[aba] }]),
  ] : null);

  if (erro && !c) return <ErroDaApi erro={erro} />;
  if (!c) return <Carregando texto="Abrindo a campanha CPTu" />;

  return (
    <div className="ficha-os campanha-cptu">
      {/* A recarga que falha depois da primeira não cala: o que está na tela pode estar velho. */}
      {erro ? <ErroDaApi erro={erro} /> : null}
      <CabecalhoDaCampanha c={c} aoMudar={carregar}
        secundario={(simulando && !!vid) || !!did || aba === 'premissas' || aba === 'analise' || aba === 'relatorio'} />
      {c.campanha.situacao === 'cancelada' && (
        <Aviso tipo="info" titulo="Campanha cancelada.">
          {c.campanha.motivo_situacao
            ? `Motivo: ${c.campanha.motivo_situacao}. ` : ''}
          As verticais, as leituras e os arquivos ficam como registro; nada novo entra, e os pontos ficaram livres
          para outra campanha da OS.
        </Aviso>
      )}
      <ReguaDaCampanha c={c} relatorio={relatorio && relatorio.situacao === c.campanha.situacao ? relatorio : null} />
      <AbasDaCampanha c={c} aba={aba} />
      {aba === 'verticais' && vid && (
        <VerticalInterpretada key={vid} c={c} verticalId={vid} vista={vista} aoMudar={carregar} aoSimular={setSimulando} />
      )}
      {aba === 'verticais' && !vid && <CampanhaVerticais c={c} gravado={gravado} aoMudar={carregar} />}
      {aba === 'dissipacoes' && did && <DissipacaoInterpretada key={did} c={c} dissipacaoId={did} aoMudar={carregar} />}
      {aba === 'dissipacoes' && !did && <CampanhaDissipacoes c={c} />}
      {aba === 'premissas' && <CampanhaPremissas c={c} aoMudar={carregar} />}
      {aba === 'analise' && <CampanhaAnalise c={c} vista={vistaDaAnalise} aoMudar={carregar} />}
      {aba === 'relatorio' && <CampanhaRelatorio c={c} aoMudar={carregar} aoRelatorio={setRelatorio} />}
      {aba === 'historico' && <CampanhaHistorico c={c} />}
      <RodapeDoMotor c={c} />
    </div>
  );
}

/* ── O cabeçalho ────────────────────────────────────────────────────── */

/** O nome da permissão `cptu.editar` como o cadastro a escreve (0254), para a ação bloqueada. */
export const PERMISSAO_DE_EDITAR_CPTU = 'Importar verticais e dissipações, processar e montar o relatório da campanha CPTu';

/** Por que a campanha não muda mais: a frase da ação bloqueada (P-UI-24). */
export function motivoDaCampanhaParada(situacao: string): string {
  return situacao === 'cancelada'
    ? 'A campanha cancelada não recebe importação nem mudança: o que entrou fica como registro.'
    : `A campanha está «${degrauDe(SITUACAO_DA_CAMPANHA_CPTU, situacao).texto}»: só a campanha em rascunho `
      + 'ou devolvida recebe importação e mudança.';
}

/**
 * O motivo de um bloqueio das telas da 313, com a frase da `AcaoBloqueada` da casa (P-UI-24): a permissão que falta
 * (ou a persona vestida), ou a campanha parada.
 */
export function motivoDoBloqueio(
  bloqueio: 'permissao' | 'parada', oQueFaz: string, situacao: string, persona: string | null,
): string {
  if (bloqueio === 'parada') return motivoDaCampanhaParada(situacao);
  return persona
    ? `Você está atuando como ${persona}, que ${oQueFaz}. Volte a ser você na tarja do topo.`
    : `Seu perfil ${oQueFaz} — falta a permissão «${PERMISSAO_DE_EDITAR_CPTU}». Peça a quem administra os usuários.`;
}

/**
 * Um grupo de ações bloqueadas pelo mesmo motivo (rodada do agent-browser da 313; P-UI-24): as ações à vista e
 * bloqueadas, no desenho da `AcaoBloqueada` da casa, e o motivo escrito UMA vez para o grupo — cada botão o leva no
 * `title` e no `aria-describedby`. Com `id`, outra ação bloqueada da tela, longe do grupo, aponta o mesmo motivo
 * (`AcaoBloqueadaPeloMotivo`).
 */
export function AcoesBloqueadas({ textos, motivo, id }: { textos: readonly string[]; motivo: string; id?: string }) {
  const proprio = useId();
  const idDoMotivo = id ?? proprio;
  return (
    <span className="acao-bloqueada cptu-acoes-bloqueadas">
      {textos.map((t) => (
        <button key={t} type="button" className="botao" disabled title={motivo} aria-describedby={idDoMotivo}>{t}</button>
      ))}
      <small id={idDoMotivo} className="det">{motivo}</small>
    </span>
  );
}

/** Uma ação bloqueada cujo motivo já está escrito na tela, no grupo principal (`idDoMotivo`): à vista, sem repetir. */
export function AcaoBloqueadaPeloMotivo({ texto, motivo, idDoMotivo }: { texto: string; motivo: string; idDoMotivo: string }) {
  return (
    <span className="acao-bloqueada">
      <button type="button" className="botao" disabled title={motivo} aria-describedby={idDoMotivo}>{texto}</button>
    </span>
  );
}

/**
 * Uma ação SECUNDÁRIA bloqueada («Devolver», «Abrir revisão», «Anexar documento»; P-UI-24 vale só para a ação
 * principal, P-UI-10 um primário por vez): o mesmo desenho da `AcaoBloqueada` da casa, mas em `botao secundario`, e
 * com `aria-disabled` (P-UI-25) — focável, e o motivo escrito ao lado chega pelo `aria-describedby`.
 */
export function AcaoSecundariaBloqueada({ texto, motivo }: { texto: string; motivo: string }) {
  const id = useId();
  return (
    <span className="acao-bloqueada" style={{ flex: '0 0 auto' }}>
      <button type="button" className="botao secundario" aria-disabled="true" aria-describedby={id}>{texto}</button>
      <small id={id} className="det">{motivo}</small>
    </span>
  );
}

/**
 * «Campanha CPTU-01 · OS 2609350», o selo e UMA ação primária
 * («Importar arquivos»); cancelar é secundária e mora no menu «⋯», como na
 * OS (P-UI-33). A importação é no computador (no celular a campanha é só
 * consulta). Sem `cptu.editar`, ou com a campanha que já foi para a
 * verificação (ou cancelada), «Importar arquivos» fica à vista, desativado,
 * com o motivo ao lado (P-UI-24) — a API recusaria do mesmo jeito.
 */
function CabecalhoDaCampanha({ c, aoMudar, secundario = false }: {
  c: CampanhaCompleta; aoMudar(): Promise<void>;
  /** Outro primário está na tela (o «Gravar» do Simular): «Importar arquivos» vira secundário (P-UI-10). */
  secundario?: boolean;
}) {
  const { pode, eu } = useSessao();
  const estreito = useEstreito();
  const [cancelando, setCancelando] = useState(false);
  const editavel = campanhaEditavel(c.campanha.situacao);
  const codigo = codigoDaCampanha(c.campanha.sequencia);
  let acoes: ReactNode = null;
  if (estreito) {
    acoes = null;
  } else if (!pode('cptu.editar')) {
    acoes = (
      <AcaoBloqueada texto="Importar arquivos" oQueFaz="não importa os arquivos do CPTu"
        permissao={PERMISSAO_DE_EDITAR_CPTU} persona={eu?.atuandoPor ? eu.nome : null} />
    );
  } else if (!editavel) {
    acoes = <AcaoBloqueada texto="Importar arquivos" motivo={motivoDaCampanhaParada(c.campanha.situacao)} />;
  } else {
    acoes = (
      <>
        <Link className={secundario ? 'botao secundario' : 'botao'} to={`/cptu/${c.campanha.id}/importar`}>Importar arquivos</Link>
        {!c.campanha.relatorio_id && (
          <MenuDaOs rotulo="Mais ações da campanha">
            <button type="button" className="item-menu" onClick={() => setCancelando(true)}>
              Cancelar a campanha
            </button>
          </MenuDaOs>
        )}
      </>
    );
  }
  return (
    <header className="cabeca-os">
      <div className="cabeca-os-id">
        <div className="cabeca-os-linha">
          <h1>
            Campanha <span className="mono">{codigo}</span>
            {' · '}OS <span className="mono">{c.os.numero}</span>
          </h1>
          <SeloDaCampanha situacao={c.campanha.situacao} />
        </div>
        <p className="cabeca-os-sub">
          {c.campanha.titulo}
          {c.os.cliente && <> · {c.os.cliente}</>}
          {c.os.obra && <> · {c.os.obra}</>}
        </p>
      </div>
      {acoes && <div className="cabeca-os-acoes">{acoes}</div>}
      {cancelando && (
        <JanelaDeCancelar c={c} aoFechar={() => setCancelando(false)}
          aoPronto={async () => { setCancelando(false); await aoMudar(); }} />
      )}
    </header>
  );
}

/**
 * Cancelar com motivo. O que já entrou fica (RN-253): a campanha só deixa de receber, e as verticais ativas dela
 * passam a canceladas — os pontos ficam livres para outra campanha (rodada final da 312). O foco abre no motivo; o
 * Enter nele quebra a linha, e só o botão cancela (ação que não se desfaz, P-UI-28).
 */
function JanelaDeCancelar({ c, aoFechar, aoPronto }: {
  c: CampanhaCompleta; aoFechar(): void; aoPronto(): Promise<void>;
}) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const falta = motivo.trim().length < 10 ? 'Diga o motivo (pelo menos 10 caracteres).' : null;
  const gravar = async () => {
    if (falta || ocupado) return;
    setOcupado(true); setErro(null);
    try { await cancelarCampanha(c.campanha.id, motivo.trim()); await aoPronto(); }
    catch (e) { setErro(e); setOcupado(false); }
  };
  return (
    <Dialogo aberto titulo="Cancelar a campanha" rotulo={`${codigoDaCampanha(c.campanha.sequencia)} · OS ${c.os.numero}`}
      aoFechar={aoFechar}>
      <FormularioDaJanela aoEnviar={() => { void gravar(); }}>
        <ErroDaApi erro={erro} />
        <p className="sub">
          As verticais, as leituras e os arquivos do equipamento ficam como estão, para o histórico. A
          campanha cancelada não recebe importação nem fotos, e os pontos dela ficam livres para outra campanha
          da OS.
        </p>
        <Area rotulo="Motivo" rows={3} value={motivo} maxLength={2000}
          dica="Pelo menos 10 caracteres: o motivo vai para a trilha."
          onChange={(e) => setMotivo(e.target.value)} />
        {falta && <p className="cptu-falta" role="status">{falta}</p>}
        <div className="rodape-dialogo">
          <button type="submit" className="botao perigo" aria-disabled={!!falta || ocupado}>
            {ocupado ? 'Cancelando…' : 'Cancelar a campanha'}
          </button>
          <button type="button" className="botao secundario" onClick={aoFechar}>Voltar</button>
        </div>
      </FormularioDaJanela>
    </Dialogo>
  );
}

/* ── A régua das etapas (R11) ───────────────────────────────────────── */

type Estado = 'feito' | 'andamento' | 'pendente' | 'atencao';

/** O símbolo e a palavra de cada estado — nunca só a cor (§18). */
const ESTADO: { [e in Estado]: { sim: string; palavra: string } } = {
  feito: { sim: '✓', palavra: 'feito' },
  andamento: { sim: '◐', palavra: 'em andamento' },
  pendente: { sim: '○', palavra: 'pendente' },
  atencao: { sim: '▲', palavra: 'atenção' },
};

interface Etapa { chave: string; nome: string; conta: string; estado: Estado; para?: string }

/**
 * A conta da «Processada» (R16): o que a TELA gravou de interpretação — as premissas da campanha e as
 * exceções e a edição de uma vertical com a marca da tela (`premissas_da_tela_em`, `edicao_da_tela_em`: a
 * importação não marca, e as premissas do projeto aplicadas por cima tiram a marca), e que ainda valem; as
 * camadas vigentes; e as dissipações com uma escolha gravada depois da importação (`escolhas_gravadas`).
 * Null: nada gravado ainda.
 */
function interpretacaoGravada(c: CampanhaCompleta): string | null {
  const partes: string[] = [];
  if (c.campanha.premissas_da_tela_em && Object.keys(c.campanha.premissas ?? {}).length > 0) {
    partes.push('premissas da campanha');
  }
  const verticais = c.verticais.filter((v) => v.situacao === 'ativa' && (
    (v.premissas_da_tela_em && Object.keys(excecoesDaVertical(v)).length > 0)
    || (v.edicao_da_tela_em && v.edicao !== null)
    || v.n_camadas > 0)).length;
  if (verticais > 0) {
    partes.push(plural(verticais, 'vertical com exceção, edição ou camadas', 'verticais com exceção, edição ou camadas'));
  }
  const dissipacoes = c.dissipacoes.filter((d) => d.escolhas_gravadas).length;
  if (dissipacoes > 0) partes.push(plural(dissipacoes, 'dissipação ajustada', 'dissipações ajustadas'));
  return partes.length > 0 ? partes.join(' · ') : null;
}

/**
 * As quatro etapas do relatório (onda 315): Verificação, Aprovação do RT, Emitido e No Portal, com o estado do
 * relatório (quem e quando) quando a aba Relatório já o leu; sem ele, o que a situação da campanha diz.
 */
function etapasDoRelatorio(c: CampanhaCompleta, r: RelatorioCompleto | null): Etapa[] {
  const s = c.campanha.situacao;
  const rel = r?.relatorio ?? null;
  const aberta = r ? revisaoAberta(r) : false;
  const para = `/cptu/${c.campanha.id}/relatorio`;
  const verificado = rel?.verificado ?? null;
  let verificacao: Etapa;
  if (s === 'devolvida' && !aberta) {
    verificacao = { chave: 'verificacao', nome: 'Verificação', para, estado: 'atencao',
      conta: `devolvida: ${rel?.motivoDevolucao ?? c.campanha.motivo_situacao ?? 'o motivo está no histórico'}` };
  } else if (s === 'em_verificacao' && !verificado) {
    verificacao = { chave: 'verificacao', nome: 'Verificação', para, estado: 'andamento', conta: 'aguardando' };
  } else if (['em_verificacao', 'aprovada', 'emitida'].includes(s)) {
    verificacao = { chave: 'verificacao', nome: 'Verificação', para, estado: 'feito',
      conta: verificado ? `verificado por ${verificado.nome}` : 'verificado' };
  } else {
    verificacao = { chave: 'verificacao', nome: 'Verificação', para, estado: 'pendente', conta: 'não enviada' };
  }
  let aprovacao: Etapa;
  if (['aprovada', 'emitida'].includes(s)) {
    aprovacao = { chave: 'aprovacao', nome: 'Aprovação do RT', para, estado: 'feito',
      conta: rel?.aprovado ? `aprovado por ${rel.aprovado.nome}` : 'aprovado' };
  } else if (s === 'em_verificacao' && verificado) {
    aprovacao = { chave: 'aprovacao', nome: 'Aprovação do RT', para, estado: 'andamento', conta: 'aguardando o RT' };
  } else {
    aprovacao = { chave: 'aprovacao', nome: 'Aprovação do RT', para, estado: 'pendente', conta: 'não aprovado' };
  }
  const numero = rel?.numero ?? c.campanha.relatorio_numero;
  let emitido: Etapa;
  if ((s === 'emitida' || aberta) && rel?.emitido) {
    emitido = { chave: 'emitido', nome: 'Emitido', para, estado: 'feito',
      conta: `${numero} ${revisao(rel.revisao)} · ${data(rel.emitido.em)}${aberta ? ` · ${revisao(rel.revisao + 1)} em elaboração` : ''}` };
  } else if (s === 'emitida') {
    emitido = { chave: 'emitido', nome: 'Emitido', para, estado: 'feito', conta: numero ?? 'emitido' };
  } else if (s === 'aprovada') {
    emitido = { chave: 'emitido', nome: 'Emitido', para, estado: 'atencao', conta: 'a emissão não terminou' };
  } else {
    emitido = { chave: 'emitido', nome: 'Emitido', para, estado: 'pendente',
      conta: numero ? `${numero} · não emitido` : 'sem relatório' };
  }
  const portal: Etapa = rel?.disponibilizadoEm && (s === 'emitida' || aberta)
    ? { chave: 'portal', nome: 'No Portal', para, estado: 'feito', conta: `desde ${data(rel.disponibilizadoEm)}` }
    : { chave: 'portal', nome: 'No Portal', para, estado: 'pendente', conta: 'não publicado' };
  return [verificacao, aprovacao, emitido, portal];
}

function etapasDaCampanha(c: CampanhaCompleta, r: RelatorioCompleto | null): Etapa[] {
  const n = c.verticais.length;
  const d = c.dissipacoes.length;
  const interpretada = interpretacaoGravada(c);
  const s = c.campanha.situacao;
  const aberta = r ? revisaoAberta(r) : false;
  /* «Processada» fica «feito» no envio para a verificação (doc 296 §7); devolvida ou numa revisão aberta, volta a
     andar. */
  const enviada = ['em_verificacao', 'aprovada', 'emitida'].includes(s);
  const processada: Etapa = enviada
    ? { chave: 'processada', nome: 'Processada', conta: 'enviada para a verificação', estado: 'feito' }
    : s === 'devolvida'
      ? { chave: 'processada', nome: 'Processada', conta: interpretada ?? 'devolvida para correção', estado: 'andamento' }
      : aberta
        ? { chave: 'processada', nome: 'Processada', conta: interpretada ?? 'revisão em elaboração', estado: 'andamento' }
        : { chave: 'processada', nome: 'Processada', conta: interpretada ?? 'interpretação a fazer',
          estado: interpretada ? 'andamento' : 'pendente' };
  return [
    {
      chave: 'importada', nome: 'Importada', para: `/cptu/${c.campanha.id}`,
      conta: n === 0 ? 'nenhuma vertical ainda'
        : `${plural(n, 'vertical', 'verticais')}${d ? ` · ${plural(d, 'dissipação', 'dissipações')}` : ''}`,
      estado: n > 0 ? 'feito' : 'pendente',
    },
    processada,
    ...etapasDoRelatorio(c, r),
  ];
}

/**
 * A régua — o mesmo desenho da OS (P-UI-33): seis etapas na ordem do
 * serviço, cada uma com a conta e o estado em símbolo e palavra. A
 * «Importada» leva à aba Verticais e as quatro do relatório, à aba
 * Relatório (315). No celular, uma linha só e «6 etapas ▾» (a folha da 282).
 */
function ReguaDaCampanha({ c, relatorio }: { c: CampanhaCompleta; relatorio: RelatorioCompleto | null }) {
  const [aberta, setAberta] = useState(false);
  const etapas = etapasDaCampanha(c, relatorio);
  const iAtual = Math.max(0, etapas.findIndex((e) => e.estado !== 'feito'));
  const destaque = etapas[iAtual];
  const idDaRegua = `regua-cptu-${c.campanha.id}`;
  const corpoDaEtapa = (e: Etapa, i: number): ReactNode => {
    const est = ESTADO[e.estado];
    return (
      <>
        <span className="etapa-os-nome">{i + 1} · {e.nome}</span>
        <span className="etapa-os-conta">
          <span className="etapa-os-sim" aria-hidden="true">{est.sim}</span>
          <span className="so-leitor">{est.palavra}: </span>
          {e.conta}
        </span>
      </>
    );
  };
  return (
    <div className={`regua-os-caixa regua-cptu${aberta ? ' aberta' : ''}`}>
      <div className="regua-os-compacta">
        <div className="regua-os-trilho" aria-hidden="true">
          {etapas.map((e, i) => (
            <span key={e.chave} className={`${e.estado}${i === iAtual ? ' atual' : ''}`} />
          ))}
        </div>
        <div className="regua-os-linha">
          <span className="regua-os-agora">
            <b>{iAtual + 1} · {destaque.nome}</b>{' '}
            <span className={`etapa-os-conta ${destaque.estado}`}>
              <span className="etapa-os-sim" aria-hidden="true">{ESTADO[destaque.estado].sim}</span>
              <span className="so-leitor">{ESTADO[destaque.estado].palavra}: </span>
              {destaque.conta}
            </span>
          </span>
          <button type="button" className="regua-os-abrir" aria-expanded={aberta}
            aria-controls={idDaRegua} onClick={() => setAberta((a) => !a)}>
            {etapas.length} etapas {aberta ? '▴' : '▾'}
          </button>
        </div>
      </div>
      <ol className="regua-os" id={idDaRegua} aria-label="Etapas da campanha">
        {etapas.map((e, i) => (
          <li key={e.chave} className={`etapa-os ${e.estado}${i === iAtual ? ' atual' : ''}`}
            data-etapa={e.chave}>
            {e.para
              ? <Link to={e.para}>{corpoDaEtapa(e, i)}</Link>
              : <span className="etapa-cptu">{corpoDaEtapa(e, i)}</span>}
          </li>
        ))}
      </ol>
    </div>
  );
}

/* ── As abas ────────────────────────────────────────────────────────── */

function AbasDaCampanha({ c, aba }: { c: CampanhaCompleta; aba: AbaDaCampanha }) {
  const faixa = useRef<HTMLElement>(null);
  /* No celular a faixa rola de lado; a aba aberta vem para a vista (só a faixa rola, nunca a página). */
  useEffect(() => {
    const nav = faixa.current;
    const sel = nav?.querySelector<HTMLElement>('.aba-os.sel');
    if (!nav || !sel || nav.scrollWidth <= nav.clientWidth) return;
    nav.scrollLeft = Math.max(0, sel.offsetLeft - 16);
  }, [aba]);
  const abas: { aba: AbaDaCampanha; cont?: number }[] = [
    { aba: 'verticais', cont: c.verticais.length },
    { aba: 'dissipacoes', cont: c.dissipacoes.length },
    { aba: 'premissas' },
    { aba: 'analise' },
    { aba: 'relatorio' },
    { aba: 'historico' },
  ];
  return (
    <nav className="abas-os" aria-label="Abas da campanha" ref={faixa}>
      {abas.map((a) => (
        <Link key={a.aba} to={rotaDaAbaDaCampanha(c.campanha.id, a.aba)}
          className={`aba-os${a.aba === aba ? ' sel' : ''}`}
          aria-current={a.aba === aba ? 'page' : undefined}>
          {NOME_DA_ABA[a.aba]}
          {a.cont !== undefined && a.cont > 0 && <span className="aba-os-cont">{a.cont}</span>}
        </Link>
      ))}
    </nav>
  );
}

/* ── O rodapé do motor ──────────────────────────────────────────────── */

/**
 * A versão do motor e se o RT já a assinou (R3, RN-121): sem a assinatura em
 * SGQ › Métodos homologados, o relatório sai preliminar (315).
 */
function RodapeDoMotor({ c }: { c: CampanhaCompleta }) {
  return (
    <p className="cptu-rodape-motor">
      Motor CPTu <span className="mono">{c.motor.versao}</span>
      {c.motor.homologada
        ? ' · homologado'
        : <span className="pendente"> · aguardando a assinatura do RT (SGQ › Métodos homologados)</span>}
    </p>
  );
}
